<template>
    <div class="organization-editor">
        <v-dialog
            v-model="dialog"
            max-width="680"
            scrollable
            :persistent="busy || confirmDialog"
            :aria-labelledby="`${formId}-title`"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-if="tableView || !deleteOnly"
                    class="branch-trigger"
                    :class="{ 'branch-trigger--tree': !tableView, 'branch-trigger--icon': iconOnly && tableView }"
                    :color="tableView ? '#f0784c' : '#db5120'"
                    :outlined="tableView"
                    :small="tableView"
                    :icon="iconOnly && tableView"
                    :disabled="busy"
                    :aria-label="iconOnly ? 'Update organization' : undefined"
                    :title="iconOnly ? 'Update organization' : undefined"
                    elevation="0"
                    v-bind="attrs"
                    v-on="on"
                >
                    <v-icon v-if="iconOnly && tableView">{{ mdiPencilOutline }}</v-icon>
                    <template v-else>{{ tableView ? 'Update Organization' : `Update ${organizationName}` }}</template>
                </v-btn>
                <v-btn
                    v-else
                    class="branch-trigger branch-trigger--tree branch-trigger--danger"
                    color="#ffaaa3"
                    outlined
                    elevation="0"
                    :disabled="busy"
                    @click="openDeletion"
                    >Delete {{ organizationName }}</v-btn
                >
            </template>
            <v-card class="branch-dialog" dark elevation="0" :aria-busy="busy ? 'true' : 'false'">
                <v-card-title class="branch-dialog__header">
                    <p class="eyebrow">Organization workspace</p>
                    <h2 :id="`${formId}-title`">Organization profile</h2>
                    <p>Keep your organization name and location up to date.</p>
                </v-card-title>
                <v-card-text class="branch-dialog__body">
                    <section class="organization-context" aria-label="Current organization">
                        <span class="eyebrow">Editing organization</span>
                        <h3>{{ organizationName }}</h3>
                        <dl>
                            <div
                                ><dt>Current parent</dt><dd>{{ parentName }}</dd></div
                            >
                        </dl>
                    </section>
                    <!-- The address editor owns its form; the name form must remain a sibling. -->
                    <form :id="formId" class="name-section" novalidate @submit.prevent="updateOrg">
                        <label :for="`${formId}-name`">Organization name</label>
                        <p :id="`${formId}-name-help`" class="section-help">Use a name your team will recognize.</p>
                        <input
                            :id="`${formId}-name`"
                            v-model="orgName"
                            name="organizationName"
                            type="text"
                            autocomplete="organization"
                            required
                            :disabled="busy || confirmDialog"
                            :aria-invalid="nameError ? 'true' : 'false'"
                            :aria-describedby="`${formId}-name-help${nameError ? ` ${formId}-name-error` : ''}`"
                            @blur="nameTouched = true"
                        />
                        <p v-if="nameError" :id="`${formId}-name-error`" class="field-error" role="alert">{{
                            nameError
                        }}</p>
                    </form>
                    <fieldset class="address-section" :disabled="busy || confirmDialog">
                        <legend>Location details</legend>
                        <p class="section-help">Review the current address and update only what has changed.</p>
                        <org-address
                            v-if="dialog"
                            :key="formVersion"
                            :current-level="currentLevel"
                            :org-name="trimmedName"
                            view="update"
                            label="Organization address"
                            @address="receiveAddress"
                            @valid="addressValid = $event"
                            @submit.native.prevent.stop
                            @keydown.enter.native="addressEnter"
                        />
                    </fieldset>
                    <section class="change-summary" aria-label="Review changes" aria-live="polite" aria-atomic="true">
                        <h3>Review changes</h3>
                        <p v-if="!changes.length">No changes to save. Your organization profile is up to date.</p>
                        <dl v-else>
                            <div v-for="change in changes" :key="change.field">
                                <dt>{{ change.label }}</dt>
                                <dd
                                    ><span class="previous-value">{{ change.before || 'Not set' }}</span>
                                    <span aria-hidden="true"> → </span><span class="sr-only"> changes to </span>
                                    <strong>{{ change.after || 'Not set' }}</strong></dd
                                >
                            </div>
                        </dl>
                        <p
                            v-if="changes.length && (!trimmedName || !address || addressValid !== true)"
                            class="review-help"
                        >
                            Complete the organization name and required address fields before saving.
                        </p>
                    </section>
                    <p v-if="!organizationId" class="form-error" role="alert">
                        No organization is selected. Select an organization before saving.
                    </p>
                    <p v-if="saveError" class="form-error" role="alert">{{ saveError }}</p>
                    <section v-if="tableView" class="deletion-section" aria-label="Delete organization">
                        <div
                            ><h3>Delete organization</h3
                            ><p>Permanently delete this organization. This cannot be undone.</p></div
                        >
                        <v-btn
                            class="branch-danger open-deletion"
                            type="button"
                            outlined
                            elevation="0"
                            :disabled="busy"
                            @click="openDeletion"
                            >Delete organization</v-btn
                        >
                    </section>
                </v-card-text>
                <v-card-actions class="branch-dialog__actions">
                    <v-btn
                        class="branch-secondary cancel-edit"
                        type="button"
                        text
                        :disabled="busy || confirmDialog"
                        @click="closeEditor"
                        >Cancel</v-btn
                    >
                    <v-btn
                        class="branch-primary save-organization"
                        type="submit"
                        :form="formId"
                        color="#db5120"
                        elevation="0"
                        :loading="saving"
                        :disabled="!canSave"
                        >Save changes</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="confirmDialog"
            max-width="680"
            scrollable
            :persistent="busy"
            :aria-labelledby="`${formId}-delete-title`"
        >
            <v-card class="branch-dialog deletion-dialog" dark elevation="0" :aria-busy="busy ? 'true' : 'false'">
                <v-card-title class="branch-dialog__header">
                    <p class="eyebrow">Permanent action</p>
                    <h2 :id="`${formId}-delete-title`">Delete organization?</h2>
                    <p>This action cannot be undone.</p>
                </v-card-title>
                <v-card-text class="branch-dialog__body">
                    <section class="organization-context" aria-label="Organization to delete">
                        <h3>{{ organizationName }}</h3>
                        <dl>
                            <div
                                ><dt>Current parent</dt><dd>{{ parentName }}</dd></div
                            >
                        </dl>
                    </section>
                    <p class="deletion-warning">You are deleting this organization, not just changing its profile.</p>
                    <p v-if="dialog" class="section-help">
                        Any unsaved profile changes will not be applied. Cancel to return to editing.
                    </p>
                    <form :id="`${formId}-delete`" class="confirmation-section" novalidate @submit.prevent="deleteOrg">
                        <label :for="`${formId}-confirmation`">Type the exact organization name to confirm</label>
                        <p :id="`${formId}-confirmation-help`" class="section-help"
                            >Enter <strong>{{ organizationName }}</strong
                            >, including capitalization and spaces.</p
                        >
                        <input
                            :id="`${formId}-confirmation`"
                            v-model="confirmDelete"
                            name="confirmOrganizationName"
                            type="text"
                            autocomplete="off"
                            :disabled="busy"
                            :aria-describedby="`${formId}-confirmation-help`"
                        />
                    </form>
                    <p v-if="!organizationId" class="form-error" role="alert">
                        No organization is selected. Select an organization before deleting.
                    </p>
                    <p v-if="deletionError" class="form-error" role="alert">{{ deletionError }}</p>
                </v-card-text>
                <v-card-actions class="branch-dialog__actions">
                    <v-btn
                        class="branch-secondary cancel-deletion"
                        type="button"
                        text
                        :disabled="busy"
                        @click="cancelDeletion"
                        >Cancel</v-btn
                    >
                    <v-btn
                        class="branch-danger confirm-deletion"
                        type="submit"
                        :form="`${formId}-delete`"
                        outlined
                        elevation="0"
                        :loading="deleting"
                        :disabled="!canDelete"
                        >Delete organization</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import gql from 'graphql-tag'
import OrgAddress from './address.vue'
import { mdiPencilOutline } from '@mdi/js'

const addressFields = {
    line1: 'Address line 1',
    line2: 'Address line 2',
    city: 'City',
    state: 'State / province',
    postalCode: 'ZIP / postal code',
    country: 'Country'
}
const normalize = (value) => (value == null ? '' : String(value).trim())
const addressSnapshot = (address) =>
    Object.fromEntries(
        Object.keys(addressFields).map((field) => [field, address && address[field] != null ? address[field] : null])
    )

export default {
    name: 'UpdateOrg',
    components: { OrgAddress },
    props: {
        currentLevel: { type: Object, required: true },
        tableView: { type: Boolean, required: true },
        deleteOnly: { type: Boolean, required: true },
        iconOnly: { type: Boolean, default: false }
    },
    data() {
        return {
            mdiPencilOutline,
            dialog: false,
            confirmDialog: false,
            confirmDelete: '',
            orgName: '',
            address: null,
            addressValid: null,
            nameTouched: false,
            saving: false,
            deleting: false,
            saveError: '',
            deletionError: '',
            deletionVersion: 0,
            formVersion: 0
        }
    },
    computed: {
        formId() {
            return `update-organization-${this._uid}`
        },
        organizationId() {
            return this.currentLevel.baseAccountId
        },
        organizationName() {
            return this.currentLevel.baseAccountName || 'this organization'
        },
        parentName() {
            if (!this.currentLevel.parentOrgId) return 'Top-level organization'
            return (
                this.currentLevel.parentOrgName ||
                (this.currentLevel.parentOrg && this.currentLevel.parentOrg.organizationName) ||
                'Parent organization'
            )
        },
        contextKey() {
            return JSON.stringify([this.currentLevel, this.tableView, this.deleteOnly])
        },
        trimmedName() {
            return this.orgName.trim()
        },
        nameError() {
            return this.nameTouched && !this.trimmedName ? 'Enter an organization name.' : ''
        },
        busy() {
            return this.saving || this.deleting
        },
        deletionAvailable() {
            return this.tableView || this.deleteOnly
        },
        changes() {
            const changes = []
            const beforeName = normalize(this.currentLevel.baseAccountName)
            if (beforeName !== this.trimmedName)
                changes.push({ field: 'name', label: 'Organization name', before: beforeName, after: this.trimmedName })
            const original = addressSnapshot(this.currentLevel.address)
            if (this.address)
                Object.keys(addressFields).forEach((field) => {
                    const before = normalize(original[field])
                    const after = normalize(this.address[field])
                    if (before !== after) changes.push({ field, label: addressFields[field], before, after })
                })
            return changes
        },
        canSave() {
            return (
                this.dialog &&
                (this.tableView || !this.deleteOnly) &&
                !this.confirmDialog &&
                !this.busy &&
                !!this.organizationId &&
                !!this.trimmedName &&
                !!this.address &&
                this.addressValid === true &&
                this.changes.length > 0
            )
        },
        confirmDeletion() {
            return !this.currentLevel.baseAccountName || this.confirmDelete !== this.currentLevel.baseAccountName
        },
        canDelete() {
            return (
                this.confirmDialog &&
                this.deletionAvailable &&
                !this.busy &&
                !!this.organizationId &&
                !this.confirmDeletion
            )
        }
    },
    watch: {
        dialog() {
            this.resetForm()
        },
        currentLevel: {
            deep: true,
            immediate: true,
            handler() {
                this.resetForm()
            }
        },
        tableView() {
            this.resetContext()
        },
        deleteOnly() {
            this.resetContext()
        },
        confirmDialog(open) {
            if (!open) {
                this.deletionVersion += 1
                this.confirmDelete = ''
                this.deletionError = ''
            }
        }
    },
    beforeDestroy() {
        this.formVersion += 1
    },
    methods: {
        resetForm() {
            this.formVersion += 1
            this.orgName = this.currentLevel.baseAccountName || ''
            this.address = addressSnapshot(this.currentLevel.address)
            this.addressValid = null
            this.nameTouched = false
            this.saveError = ''
            this.deletionError = ''
            this.confirmDelete = ''
            this.confirmDialog = false
        },
        resetContext() {
            this.resetForm()
            if (!this.tableView && this.deleteOnly) this.dialog = false
        },
        receiveAddress(value) {
            this.address = value ? addressSnapshot(value) : null
        },
        closeEditor() {
            if (!this.busy && !this.confirmDialog) this.dialog = false
        },
        openDeletion() {
            if (this.busy || !this.deletionAvailable) return
            if (!this.dialog) this.resetForm()
            this.deletionVersion += 1
            this.confirmDelete = ''
            this.deletionError = ''
            this.confirmDialog = true
        },
        cancelDeletion() {
            if (!this.busy) this.confirmDialog = false
        },
        addressEnter(event) {
            // Enter selects autocomplete options; only plain text inputs may save the profile.
            if (
                event.isComposing ||
                event.defaultPrevented ||
                event.target.tagName !== 'INPUT' ||
                event.target.closest('.v-select, [role="combobox"]')
            )
                return
            event.preventDefault()
            this.updateOrg()
        },
        failureMessage(action, detail) {
            const reason = typeof detail === 'string' && detail.trim() ? ` ${detail}` : ''
            return `Could not ${action} the organization.${reason} Please try again.`
        },
        async updateOrg() {
            if (!this.dialog || this.busy || this.confirmDialog) return
            this.nameTouched = true
            if (!this.canSave) return
            this.saving = true
            this.saveError = ''
            const version = this.formVersion
            const context = this.contextKey
            const name = this.trimmedName
            const isCurrent = () => this.dialog && version === this.formVersion && context === this.contextKey
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation UpdateAccountOrganization($input: AccountOrganizationInput!, $orgId: String!) {
                            updateAccountOrganization(input: $input, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        input: {
                            organizationName: name,
                            parentOrgId: this.currentLevel.parentOrgId,
                            address: { ...this.address }
                        },
                        orgId: this.organizationId
                    }
                })
                if (!isCurrent()) return
                const result = response && response.data && response.data.updateAccountOrganization
                if (result && result.success === true) {
                    this.dialog = false
                    this.$emit('updated', `'${name}' successfully updated`)
                } else this.saveError = this.failureMessage('save', result && result.error)
            } catch (error) {
                if (isCurrent()) this.saveError = this.failureMessage('save', error && error.message)
            } finally {
                this.saving = false
            }
        },
        async deleteOrg() {
            if (!this.canDelete) return
            this.deleting = true
            this.deletionError = ''
            const version = this.formVersion
            const context = this.contextKey
            const name = this.currentLevel.baseAccountName
            const parentId = this.currentLevel.parentOrgId
            const deletionVersion = this.deletionVersion
            const isCurrent = () =>
                this.confirmDialog &&
                version === this.formVersion &&
                deletionVersion === this.deletionVersion &&
                context === this.contextKey
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DisableAccountOrganization($orgId: String!) {
                            disableAccountOrganization(orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: { orgId: this.organizationId }
                })
                if (!isCurrent()) return
                const result = response && response.data && response.data.disableAccountOrganization
                if (result && result.success === true) {
                    this.dialog = false
                    this.confirmDialog = false
                    this.$emit('deleted', { parentId, status: `'${name}' successfully deleted` })
                } else this.deletionError = this.failureMessage('delete', result && result.error)
            } catch (error) {
                if (isCurrent()) this.deletionError = this.failureMessage('delete', error && error.message)
            } finally {
                this.deleting = false
            }
        }
    }
}
</script>

<style scoped>
.branch-trigger,
.branch-dialog {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.branch-trigger {
    border-radius: 8px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-trigger--tree {
    display: block;
    margin: auto;
    min-width: 150px;
    max-width: 100%;
    height: auto;
    min-height: 36px;
    padding: 8px 16px;
    color: #fff;
}
.branch-trigger--icon {
    width: 36px;
    min-width: 36px !important;
    height: 36px;
    padding: 0 !important;
    border-radius: 9px;
}
.branch-trigger--icon .v-icon {
    color: #ffb396;
}
.branch-dialog.v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    border: 1px solid #454953;
    border-radius: 12px;
    background: #282b33;
    color: #f5f5f7;
}
.branch-dialog__header {
    flex: 0 0 auto;
    display: block;
    padding: 20px 24px;
    border-bottom: 1px solid #454953;
}
.branch-dialog h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
}
.branch-dialog__header p {
    margin: 8px 0 0;
    color: #bfc3ce;
    font-size: 12px;
    line-height: 1.5;
}
.branch-dialog .eyebrow {
    margin: 0 0 8px;
    color: #f0784c;
    font-weight: 600;
}
.branch-dialog .branch-dialog__body {
    min-height: 0;
    padding: 20px 24px;
    overflow-y: auto;
    color: #d4d6dd;
    font-size: 12px;
    line-height: 1.6;
    scrollbar-color: #f0784c #22252d;
    scrollbar-width: thin;
}
.branch-dialog__body::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.branch-dialog__body::-webkit-scrollbar-track {
    background: #22252d;
    border-radius: 9px;
}
.branch-dialog__body::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #db5120);
    border: 2px solid #22252d;
    border-radius: 9px;
}
.branch-dialog__body::-webkit-scrollbar-thumb:hover {
    background: #f0784c;
}
.organization-context {
    margin-bottom: 20px;
    padding: 12px 14px;
    border: 1px solid #454953;
    border-radius: 8px;
    background: #22252d;
    overflow-wrap: anywhere;
}
.organization-context h3 {
    margin: 0 0 8px;
    font-size: 16px;
    color: #f5f5f7;
}
.organization-context dl,
.change-summary dl {
    margin: 0;
}
.organization-context dl > div,
.change-summary dl > div {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    gap: 12px;
    margin-top: 6px;
}
.branch-dialog dt,
.previous-value {
    color: #bfc3ce;
}
.branch-dialog dd {
    margin: 0;
}
.name-section {
    margin-bottom: 20px;
}
.name-section label,
.confirmation-section label,
.address-section legend,
.change-summary h3,
.deletion-section h3 {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 600;
    color: #f5f5f7;
}
.branch-dialog .section-help {
    margin: 0 0 12px;
    color: #bfc3ce;
}
.name-section input,
.confirmation-section input {
    width: 100%;
    min-height: 40px;
    padding: 10px 12px;
    border: 1px solid #686d79;
    border-radius: 8px;
    background: #22252d;
    color: #f5f5f7;
    font: inherit;
}
.name-section input[aria-invalid='true'] {
    border-color: #ffb4ac;
}
.branch-dialog .field-error {
    margin: 6px 0 0;
    color: #ffb4ac;
}
.address-section {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}
.address-section:disabled,
.name-section input:disabled,
.confirmation-section input:disabled {
    opacity: 0.65;
}
.change-summary {
    margin-top: 16px;
    padding: 12px 14px;
    border-left: 3px solid #f0784c;
    border-radius: 0 8px 8px 0;
    background: #22252d;
    overflow-wrap: anywhere;
}
.change-summary p {
    margin: 8px 0 0;
}
.review-help {
    color: #ffcfba;
}
.deletion-section {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #68504f;
}
.deletion-section > div {
    flex: 1 1 240px;
}
.deletion-section h3,
.deletion-warning {
    color: #ffaaa3;
}
.deletion-section p {
    margin: 0;
}
.branch-dialog .form-error {
    margin: 12px 0 0;
    padding: 12px;
    border: 1px solid #a45c58;
    border-radius: 8px;
    color: #ffb4ac;
    overflow-wrap: anywhere;
}
.branch-dialog__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid #454953;
    background: #22252d;
}
.branch-dialog .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    margin: 0;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog .branch-secondary {
    color: #e0e2e8;
}
.branch-dialog .branch-primary {
    color: #fff;
}
.branch-dialog .branch-danger {
    border-color: #a45c58;
    color: #ffaaa3;
}
.branch-trigger ::v-deep .v-btn__content,
.branch-dialog ::v-deep .v-btn__content {
    white-space: normal;
    overflow-wrap: anywhere;
}
.branch-dialog ::v-deep .v-input,
.branch-dialog ::v-deep .v-label,
.branch-dialog ::v-deep input {
    font-family: inherit;
    font-size: 12px;
}
.branch-dialog ::v-deep .v-text-field--outlined .v-input__slot {
    background: #22252d;
    border-radius: 8px;
}
.branch-dialog ::v-deep .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset {
    border-color: #686d79;
}
.branch-trigger:focus-visible,
.name-section input:focus-visible,
.confirmation-section input:focus-visible,
.branch-dialog ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}
@media (max-width: 600px) {
    .branch-dialog__header,
    .branch-dialog .branch-dialog__body,
    .branch-dialog__actions {
        padding: 16px;
    }
    .organization-context dl > div,
    .change-summary dl > div {
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
    }
}
</style>
